import type { Account, BatchResult, CompareRow, CompareWeek, Visit, VisitChoice, VisitEntry, VisitKind, VisitReason } from "./api";
import { MAX_GAL } from "./pickup";
import { personName } from "./requests";
import { noteProblem } from "./visitLabels";
import type { SortColumn } from "./sort";

export const KIND_TEXT: Record<VisitKind, string> = { pickup: "Pickup", level: "Level only", no_access: "No access", skipped: "Skipped" };

/** The kinds that a person enters. A skipped visit comes only from a stop change on Today. */
export const ENTRY_KINDS: readonly VisitKind[] = ["pickup", "level", "no_access"];

export const REASON_TEXT: Record<VisitReason, string> = {
  not_full: "Not full",
  locked: "Locked",
  no_access: "No access",
  blocked: "Blocked",
  other: "Other",
};

/** The reasons of "Took nothing", in the order of the chips. */
export const NOTHING_REASONS: readonly VisitReason[] = ["not_full", "locked", "blocked", "no_access", "other"];

export const kindOf = (reason: VisitReason): VisitKind => (reason === "not_full" ? "level" : "no_access");

/** As the API: a level visit needs the gallons left, and a visit with no access has none. */
export const asksLeft = (form: Pick<VisitForm, "nothing" | "notEmpty">) => (form.nothing ? kindOf(form.nothing) === "level" : form.notEmpty);

export interface Volume {
  label: string;
  word: string;
  perGal: number;
}

const LITRES = new Set(["l", "litre", "liter", "litres", "liters"]);

/** The volume unit of the organization. The API keeps gallons; the screen shows and reads this unit. */
export function volumeOf(unit: string | null | undefined): Volume {
  return LITRES.has((unit ?? "").trim().toLowerCase()) ? { label: "L", word: "litres", perGal: 3.785411784 } : { label: "gal", word: "gallons", perGal: 1 };
}

const tenth = (value: number) => Math.round(value * 10) / 10;

export const toGal = (value: number, volume: Volume) => tenth(value / volume.perGal);

export const volumeText = (gal: number | null, volume: Volume) =>
  gal == null ? "–" : `${tenth(gal * volume.perGal).toLocaleString("en-US")} ${volume.label}`;

/** A typed amount in gallons, or an error text. */
export function parseAmount(text: string, volume: Volume, what: string): number | string {
  const value = text.trim().replace(/,/g, "");
  if (!value) return `Type the ${what}.`;
  if (!/^\d+(?:\.\d+)?$/.test(value)) return `Type the ${what} as a number, for example 120.`;
  const gal = toGal(Number(value), volume);
  if (gal > MAX_GAL) return `The ${what} cannot be more than ${volumeText(MAX_GAL, volume)}.`;
  return gal;
}

const parts = (at: Date, timeZone: string) =>
  Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  );

/** The date and the time of an instant in the time zone of the organization. */
export function localNow(at: Date, timeZone: string): { day: string; time: string } {
  const p = parts(at, timeZone);
  return { day: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

const offsetMinutes = (at: Date, timeZone: string) => {
  const p = parts(at, timeZone);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60_000);
};

const offsetText = (minutes: number) => {
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

/** A wall clock date and time of the organization as an ISO datetime with its offset. */
export function zonedIso(day: string, time: string, timeZone: string): string {
  const guess = new Date(`${day}T${time}:00Z`);
  let offset = offsetMinutes(guess, timeZone);
  offset = offsetMinutes(new Date(guess.getTime() - offset * 60_000), timeZone);
  return `${day}T${time}:00${offsetText(offset)}`;
}

/** The phone form of one visit. An empty level takes the default: an empty tank. */
export interface VisitForm {
  gallons: string;
  scrap: string;
  notEmpty: boolean;
  left: string;
  nothing: VisitReason | null;
  day: string;
  time: string;
  driverId: string;
  truckId: string;
  labels: string[];
  note: string;
}

export type FormField = "gallons" | "scrap" | "left" | "reason" | "note";

export const blankForm = (day: string, time: string, driverId = "", truckId = ""): VisitForm => ({
  gallons: "",
  scrap: "",
  notEmpty: false,
  left: "",
  nothing: null,
  day,
  time,
  driverId,
  truckId,
  labels: [],
  note: "",
});

type Built = Omit<VisitEntry, "id">;

/** The visit of the phone form without its id, or the first field with a problem. */
export function formEntry(
  form: VisitForm,
  target: { accountId: string; routeStopId?: string | null },
  timeZone: string,
  volume: Volume,
): Built | { field: FormField; error: string } {
  const built = fieldsEntry(form, target, timeZone, volume);
  const problem = noteProblem(form.note);
  return "error" in built || !problem ? built : { field: "note", error: problem };
}

function fieldsEntry(
  form: VisitForm,
  target: { accountId: string; routeStopId?: string | null },
  timeZone: string,
  volume: Volume,
): Built | { field: FormField; error: string } {
  const base: Built = {
    account_id: target.accountId,
    at: zonedIso(form.day, form.time, timeZone),
    kind: "pickup",
    ...(form.driverId ? { driver_id: form.driverId } : {}),
    ...(form.truckId ? { truck_id: form.truckId } : {}),
    ...(target.routeStopId ? { route_stop_id: target.routeStopId } : {}),
    ...(form.labels.length ? { labels: form.labels } : {}),
    ...(form.note.trim() ? { note: form.note.trim() } : {}),
  };
  if (form.nothing) {
    const kind = kindOf(form.nothing);
    if (kind === "no_access") return { ...base, kind, reason: form.nothing };
    const left = parseAmount(form.left, volume, "gallons left in the container");
    if (typeof left === "string") return { field: "left", error: left };
    return { ...base, kind, reason: form.nothing, gallons_left: left };
  }
  const raw = parseAmount(form.gallons, volume, `total ${volume.word}`);
  if (typeof raw === "string") return { field: "gallons", error: raw };
  if (!form.scrap.trim()) return { field: "scrap", error: `Type the scrap ${volume.word}. Type 0 if there is no scrap.` };
  const scrap = parseAmount(form.scrap, volume, `scrap ${volume.word}`);
  if (typeof scrap === "string") return { field: "scrap", error: scrap };
  if (scrap > raw) return { field: "scrap", error: `Scrap ${volume.word} cannot be more than total ${volume.word}.` };
  const left = form.notEmpty ? parseAmount(form.left, volume, "gallons left") : 0;
  if (typeof left === "string") return { field: "left", error: left };
  return { ...base, raw_gal: raw, scrap_gal: scrap, gallons_left: left };
}

const amountText = (gal: number | null | undefined, volume: Volume) => (gal ? String(tenth(gal * volume.perGal)) : "");

/** The phone form of a kept visit, so that the driver can fix a visit that the server refused. */
export function entryForm(entry: Built, timeZone: string, volume: Volume): VisitForm {
  const { day, time } = localNow(new Date(entry.at), timeZone);
  const base = {
    ...blankForm(day, time, entry.driver_id ?? "", entry.truck_id ?? ""),
    left: amountText(entry.gallons_left, volume),
    labels: entry.labels ?? [],
    note: entry.note ?? "",
  };
  if (entry.kind !== "pickup") return { ...base, nothing: entry.reason ?? (entry.kind === "level" ? "not_full" : "no_access") };
  return {
    ...base,
    gallons: amountText(entry.raw_gal, volume) || "0",
    scrap: entry.scrap_gal == null ? "" : String(tenth(entry.scrap_gal * volume.perGal)),
    notEmpty: base.left !== "",
  };
}

/** True when the visit time is older than the offline window: the save then asks for a confirm (SO-956). */
export const isOldEntry = (at: string, now: Date, hours: number) => Date.parse(at) < now.getTime() - hours * 3_600_000;

export const oldEntryText = (hours: number) => `This date is more than ${hours} hours ago. The office checks entries this old.`;

/** The id of a visit stays until the API has it, so each retry of the same visit is the same request. */
export interface Attempt {
  id: string;
  entry: VisitEntry | null;
}

export const firstAttempt = (makeId: () => string): Attempt => ({ id: makeId(), entry: null });

export const attemptEntry = (attempt: Attempt, built: Built): VisitEntry => ({ ...built, id: attempt.id });

export const afterSave = (attempt: Attempt, entry: VisitEntry, ok: boolean, makeId: () => string): Attempt =>
  ok ? firstAttempt(makeId) : { id: attempt.id, entry };

/** One row of the office grid. A saved row is locked. */
export interface GridRow {
  key: string;
  id: string;
  account: string;
  accountId: string | null;
  day: string;
  time: string;
  driverId: string;
  truckId: string;
  raw: string;
  scrap: string;
  left: string;
  kind: VisitKind;
  reason: VisitReason | "";
  state: "new" | "saved" | "error";
  error: string;
}

/** A new row takes the date, the driver and the truck of the row above it. */
export function nextRow(above: GridRow | undefined, today: string, makeId: () => string): GridRow {
  return {
    key: makeId(),
    id: makeId(),
    account: "",
    accountId: null,
    day: above?.day ?? today,
    time: "",
    driverId: above?.driverId ?? "",
    truckId: above?.truckId ?? "",
    raw: "",
    scrap: "",
    left: "",
    kind: "pickup",
    reason: "",
    state: "new",
    error: "",
  };
}

export const rowEmpty = (row: GridRow) => !row.account.trim() && !row.raw.trim() && !row.scrap.trim() && !row.left.trim() && !row.reason;

export const accountLabel = (account: Pick<Account, "number" | "name">) => `${account.number} · ${account.name}`;

const compact = (text: string) => text.toLowerCase().replace(/[\s-]/g, "");

/** The account of a typed text: the label of a list option, or the account number alone. */
export function findAccount<T extends Pick<Account, "id" | "number" | "name">>(text: string, accounts: readonly T[]): T | undefined {
  const typed = text.trim();
  if (!typed) return undefined;
  const number = compact(typed.split("·")[0]);
  return accounts.find((account) => accountLabel(account) === typed) ?? accounts.find((account) => compact(account.number) === number);
}

/** Office time is optional; without it the visit is at noon, so the day stays the same in each time zone. */
export const NO_TIME = "12:00";

export function rowEntry(row: GridRow, timeZone: string, volume: Volume): VisitEntry | string {
  if (!row.accountId) return "Choose an account from the list.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(row.day)) return "Enter the date.";
  if (row.time && !/^\d{2}:\d{2}$/.test(row.time)) return "Enter the time as HH:MM, or leave it empty.";
  if (!row.driverId) return "Choose the driver.";
  const base: VisitEntry = {
    id: row.id,
    account_id: row.accountId,
    at: zonedIso(row.day, row.time || NO_TIME, timeZone),
    kind: row.kind,
    driver_id: row.driverId,
    ...(row.truckId ? { truck_id: row.truckId } : {}),
  };
  const left = row.left.trim() ? parseAmount(row.left, volume, "gallons left") : null;
  if (typeof left === "string") return left;
  if (row.kind === "pickup") {
    const raw = parseAmount(row.raw, volume, "raw gallons");
    if (typeof raw === "string") return raw;
    const scrap = row.scrap.trim() ? parseAmount(row.scrap, volume, "scrap gallons") : 0;
    if (typeof scrap === "string") return scrap;
    if (scrap > raw) return "Scrap cannot be more than the raw gallons.";
    return { ...base, raw_gal: raw, scrap_gal: scrap, gallons_left: left ?? 0 };
  }
  if (row.kind === "level") {
    if (left === null) return "Type the gallons left in the container.";
    return { ...base, reason: row.reason || "not_full", gallons_left: left };
  }
  if (!row.reason) return "Choose why there was no access.";
  return { ...base, reason: row.reason, ...(left !== null ? { gallons_left: left } : {}) };
}

/** The rows to send to Save all, and the rows with a problem that the browser found. */
export function gridBatch(rows: readonly GridRow[], timeZone: string, volume: Volume): { entries: VisitEntry[]; problems: Record<string, string> } {
  const entries: VisitEntry[] = [];
  const problems: Record<string, string> = {};
  for (const row of rows) {
    if (row.state === "saved" || rowEmpty(row)) continue;
    const entry = rowEntry(row, timeZone, volume);
    if (typeof entry === "string") problems[row.key] = entry;
    else entries.push(entry);
  }
  return { entries, problems };
}

/** The rows after Save all: each sent row without an error is saved and locked; an error row stays red. */
export function applyBatch(rows: readonly GridRow[], sent: readonly VisitEntry[], result: BatchResult, problems: Record<string, string> = {}): GridRow[] {
  const sentIds = new Set(sent.map((entry) => entry.id));
  const errors = new Map(result.errors.map((error) => [error.id, error.detail || error.code]));
  return rows.map((row) => {
    if (problems[row.key]) return { ...row, state: "error", error: problems[row.key] };
    if (!sentIds.has(row.id)) return row;
    const error = errors.get(row.id);
    return error ? { ...row, state: "error", error } : { ...row, state: "saved", error: "" };
  });
}

export const batchText = (result: BatchResult) => {
  const parts = [`${result.saved} saved`];
  if (result.unchanged) parts.push(`${result.unchanged} already saved`);
  if (result.errors.length) parts.push(`${result.errors.length} not saved`);
  return `${parts.join(", ")}.`;
};

/** The codes of a stop with waste left that a Today visit keeps as its note. */
const STOP_NOTE_TEXT: Record<string, string> = { frozen: "Frozen waste", truck_full: "The truck is full" };

/** The outcome of a saved visit in words, for example "No access: Locked" or "Skipped: Closed". */
export function outcomeText(visit: Pick<Visit, "kind" | "reason" | "note"> & Partial<Pick<Visit, "source">>): string {
  // A Today visit keeps the cause that the driver selected as its note (SO-1065).
  const said = visit.kind === "skipped" || visit.source === "today" ? (visit.note ?? "").trim() : "";
  const reason = said ? (STOP_NOTE_TEXT[said] ?? said) : visit.reason ? REASON_TEXT[visit.reason] : "";
  return reason && visit.kind !== "pickup" ? `${KIND_TEXT[visit.kind]}: ${reason}` : KIND_TEXT[visit.kind];
}

export const VISIT_COLUMNS: SortColumn<Visit>[] = [
  { key: "at", label: "Time", value: (v) => v.at, first: "desc" },
  { key: "account", label: "Account", value: (v) => v.account_number },
  { key: "driver", label: "Driver", value: (v) => v.driver_name?.toLowerCase() },
  { key: "truck", label: "Truck", value: (v) => v.truck_name },
  { key: "raw", label: "Raw", value: (v) => v.raw_gal, first: "desc", num: true },
  { key: "scrap", label: "Scrap", value: (v) => v.scrap_gal, first: "desc", num: true },
  { key: "left", label: "Level left", value: (v) => v.gallons_left, first: "desc", num: true },
  { key: "outcome", label: "Outcome", value: (v) => v.kind, order: ["pickup", "level", "no_access", "skipped"] },
];

export const ACCOUNT_VISIT_COLUMNS: SortColumn<Visit>[] = [
  { key: "at", label: "Time", value: (v) => v.at, first: "desc" },
  { key: "outcome", label: "Visit", value: (v) => v.kind, order: ["pickup", "level", "no_access", "skipped"] },
  { key: "driver", label: "Driver", value: (v) => v.driver_name?.toLowerCase() },
  { key: "truck", label: "Truck", value: (v) => v.truck_name },
  { key: "left", label: "Level left", value: (v) => v.gallons_left, first: "desc", num: true },
];

export const COMPARE_TEXT: Record<keyof Omit<CompareWeek, "week_end">, string> = {
  replaced_same: "App replaced the sheet, same gallons",
  replaced_diff: "App replaced the sheet, other gallons",
  level_only_with_sheet: "Level only in the app, pickup in the sheet",
  app_only: "Only in the app",
};

export const COMPARE_CASE_TEXT: Record<string, string> = { ...COMPARE_TEXT };

export const COMPARE_WEEK_COLUMNS: SortColumn<CompareWeek>[] = [
  { key: "week", label: "Week end", value: (w) => w.week_end, first: "desc" },
  ...(Object.keys(COMPARE_TEXT) as (keyof typeof COMPARE_TEXT)[]).map((key) => ({
    key,
    label: COMPARE_TEXT[key],
    value: (w: CompareWeek) => w[key],
    first: "desc" as const,
    num: true,
  })),
];

export const COMPARE_ROW_COLUMNS: SortColumn<CompareRow>[] = [
  { key: "account", label: "Account", value: (r) => r.account_number },
  { key: "week", label: "Week end", value: (r) => r.week_end, first: "desc" },
  { key: "sheet", label: "Sheet raw", value: (r) => r.sheet_raw, first: "desc", num: true },
  { key: "app", label: "App raw", value: (r) => r.app_raw, first: "desc", num: true },
  { key: "case", label: "Case", value: (r) => r.case },
];

export interface FieldChoice {
  id: string;
  label: string;
}

/** The drivers of the stop entry form; the signed-in user shows as "Name (you)". */
export function driverChoices(drivers: readonly VisitChoice[], you: string | null): FieldChoice[] {
  return [
    ...(you && !drivers.some((d) => d.id === you) ? [{ id: you, label: "You" }] : []),
    ...drivers.map((d) => ({ id: d.id, label: d.id === you ? `${personName(d.name)} (you)` : personName(d.name) })),
  ];
}

/** The trucks of the stop entry form, with "No truck" first. */
export function truckChoices(trucks: readonly { id: string; code: string }[], yours: string | null): FieldChoice[] {
  return [
    { id: "", label: "No truck" },
    ...(yours && !trucks.some((t) => t.id === yours) ? [{ id: yours, label: "Your truck" }] : []),
    ...trucks.map((t) => ({ id: t.id, label: t.code })),
  ];
}
